import { ReactElement } from 'react';

import styled from 'styled-components';

import { Stack } from '@components/stack';
import { Tooltip } from '@components/tooltip';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

import { IconProps } from '../icons';

export type StatusIndicatorVariantKey =
  | 'notStarted'
  | 'inProgress'
  | 'completed'
  | 'skipped'
  | 'error';

type StatusIndicatorVariant = {
  [key in StatusIndicatorVariantKey]: {
    backgroundColor: string;
    iconColor: string;
    iconHover?: string;
    emptyLine?: string;
    emptyLineHover?: string;
  };
};

const Colors: StatusIndicatorVariant = {
  notStarted: {
    backgroundColor: theme.color.backgroundLightDark,
    iconColor: theme.color.buttonIconPositive,
    iconHover: theme.color.buttonIconPositiveHover,
  },
  inProgress: {
    backgroundColor: theme.color.orangeTrans10,
    iconColor: theme.color.elementOrange100,
    iconHover: theme.color.elementOrange200,
  },
  completed: {
    backgroundColor: theme.color.greenTrans10,
    iconColor: theme.color.elementGreen100,
    iconHover: theme.color.elementGreen200,
  },
  skipped: {
    backgroundColor: theme.color.uiTrans10,
    iconColor: theme.color.elementPurple50,
    iconHover: theme.color.elementPurple50,
    emptyLineHover: theme.color.border10,
  },
  error: {
    backgroundColor: theme.color.redTrans20,
    iconColor: theme.color.elementRed100,
    emptyLine: theme.color.redTrans10,
    emptyLineHover: theme.color.redTrans20,
  },
};

export type StatusIndicatorProps = {
  variant: keyof typeof Colors;
  ratio?: number;
  icon: ReactElement<IconProps>;
  tooltip?: string;
};

export const CircularProgressbar = (props: {
  value: number;
  maxValue: number;
  strokeWidth?: number;
  variant: keyof typeof Colors;
  styles?: Record<string, any>;
  children?: React.ReactNode;
}) => {
  const { value, maxValue, strokeWidth = 6, variant, styles, children } = props;

  const radius = 50 - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = value / maxValue;

  return (
    <div style={{ position: 'relative' }}>
      <CircularProgressbarInner
        viewBox="0 0 100 100"
        width={pxToRem(size.button)}
        height={pxToRem(size.button)}
      >
        <circle
          cx="50"
          cy="50"
          r={radius}
          stroke={Colors[variant].emptyLine ?? theme.color.border10}
          strokeWidth={strokeWidth}
          fill="none"
          className="CircleEmptyLine"
        />
        <circle
          cx="50"
          cy="50"
          r={radius}
          stroke={Colors[variant].iconColor}
          strokeWidth={strokeWidth}
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={circumference * (1 - progress)}
          strokeLinecap="round"
          fill="none"
          className="CircleProgressLine"
          style={{ ...styles }}
        />
      </CircularProgressbarInner>
      {children}
    </div>
  );
};

type StatusIndicatorPropsWrapper = {
  variant: keyof typeof Colors;
};

const CircularProgressbarInner = styled.svg`
  transform: rotate(-90deg);
  position: absolute;
`;

const StatusIndicatorWrapper = styled.div<StatusIndicatorPropsWrapper>((prop) => {
  return {
    backgroundColor: Colors[prop.variant].backgroundColor,
    borderRadius: '50%',
    width: size.button,
    height: size.button,
    alignItems: 'center',
    color: Colors[prop.variant].iconColor,
    svg: {
      transition: transitionStandard('color'),
    },
    circle: {
      transition: transitionStandard('stroke'),
    },
    '&:hover': {
      cursor: 'pointer',
      color: Colors[prop.variant].iconHover || Colors[prop.variant].iconColor,
      '.CircleEmptyLine': {
        stroke:
          Colors[prop.variant].emptyLineHover ||
          Colors[prop.variant].emptyLine ||
          theme.color.border20,
      },
      '.CircleProgressLine': {
        stroke: Colors[prop.variant].iconHover || Colors[prop.variant].iconColor,
      },
    },
  };
});

type ButtonSizeProps = {
  button: number;
  icon: number;
};

export const size: ButtonSizeProps = {
  button: 32,
  icon: 16,
};

export const StatusIndicator = (props: StatusIndicatorProps) => {
  const { variant, ratio = 0, icon, tooltip } = props;

  let content = (
    <StatusIndicatorWrapper variant={variant}>
      <CircularProgressbar variant={variant} value={ratio} maxValue={1} strokeWidth={6}>
        <Stack align="center" justify="center" style={{ height: size.button }}>
          <icon.type {...icon.props} size={size.icon} />
        </Stack>
      </CircularProgressbar>
    </StatusIndicatorWrapper>
  );

  if (tooltip) {
    content = <Tooltip content={tooltip}>{content}</Tooltip>;
  }

  return content;
};
